/* eslint-disable react/no-unstable-nested-components */
import React, { ElementType, ReactNode } from 'react';
import * as _ from 'lodash';
import TableContainer from '@mui/material/TableContainer';
import Table from '@mui/material/Table';
import TableHead from '@mui/material/TableHead';
import TableRow from '@mui/material/TableRow';
import Box from '@mui/material/Box';
import Typography from '@mui/material/Typography';
import IconButton from '@mui/material/IconButton';
import TableCell from '@mui/material/TableCell';
import TableBody from '@mui/material/TableBody';
import Skeleton from '@mui/material/Skeleton';
import TableFooter from '@mui/material/TableFooter';
import Grid from '@mui/material/Grid';
import Pagination from '@mui/material/Pagination';
import Stack from '@mui/material/Stack';
import Menu from '@mui/material/Menu';
import MenuItem from '@mui/material/MenuItem';
import { styled as materialStyled } from '@mui/material/styles';
import { v4 as uuidv4 } from 'uuid';
import { Divider, PaginationItem } from '@mui/material';
import ExpandMoreSvg from '../../../assets/img/arrow-down.svg';
import { SortOrder } from '../../../utils/enums/sort-order.enum';
import { theme } from '../../../theme/theme';
import SortIconDescSvg from '../../../assets/img/sort-icon.svg';
import SortIconAscSvg from '../../../assets/img/sort-icon-up.svg';

const CustomTableHead = materialStyled(TableHead)(() => ({
  backgroundColor: theme.palette.shades.tableHeadColor,
}));

const CustomTableCell = materialStyled(TableCell)(() => ({
  borderBottom: `1px solid ${theme.palette.shades.grayBorder}`,
  padding: '0px 10px',
}));

const CustomTableBody = materialStyled(TableBody)(() => ({}));

const CustomTableRow = materialStyled(TableRow)(() => ({
  height: '40px',
  backgroundColor: theme.palette.white.main,
  '&:last-child td, &:last-child th': {
    borderBottom: 0,
  },
  borderBottom: `1px solid ${theme.palette.shades.grayBorder}`,
}));

const CustomPagination = materialStyled(Pagination)(() => ({
  fontFamily: 'Inter',
  fontSize: '15px',
  fontStyle: 'normal',
  fontWeight: 400,
  lineHeight: 'normal',
  '& .MuiPaginationItem-root': {
    borderRadius: '0px',
    border: `1px solid ${theme.palette.shades.grayBorder}`,
    margin: '0',
  },
  '& .Mui-selected': {
    backgroundColor: `${theme.palette.shades.lightBlueColor} !important`,
    fontWeight: 700,
    borderRadius: '0px',
    border: `1px solid ${theme.palette.shades.lightBlueColor}`,
    color: 'white',
    '&:hover': { '&.Mui-selected': { backgroundColor: theme.palette.secondary.light } },
  },
  '& .MuiPaginationItem-root:not(.Mui-selected)': {
    border: '1px solid rgba(0, 0, 0, 0.2)',
    color: theme.palette.text.secondary,
  },
  '& .MuiPaginationItem-previousNext': {
    display: 'flex',
    alignItems: 'center',
    gap: '4px',
    fontWeight: 600,
    color: theme.palette.primary.main,
    padding: '6px 12px',
  },
  '& .MuiPaginationItem-ellipsis': {
    display: 'flex',
    alignItems: 'center',
    width: '32px',
    height: '32px',
    color: theme.palette.primary.main,
  },
}));

export interface ColumnProps {
  key: string;
  name: string;
  customComponent?: ReactNode;
  enableSort: boolean;
  align: 'right' | 'left' | 'center' | 'inherit' | 'justify';
  width: string;
  minWidth?: number;
  resizable: boolean;
  headerAlign?: 'right' | 'left' | 'center' | 'inherit' | 'justify';
}

export interface CustomTableProps {
  isLoading: boolean;
  totalDataRows: number;
  enablePagination: boolean;
  selectedPage: number;
  enablePagePerRows: boolean;
  rowsPerList: number[];
  selectedRowsPerPage: number;
  sortBy: string;
  sortOrder: SortOrder;
  columns: ColumnProps[];
  rows: { [key: string]: string | ReactNode }[];
  handleChangePage: (_page: number) => void;
  handleChangeRowsPerPage: (_rowsPerPage: number) => void;
  handleSortByChange: (_sortBy: string) => void;
  handleSortOrderChange: (_sortOrder: SortOrder) => void;
  bottomResultsCount?: number;
  tableType?: string;
}

function CustomTable({
  isLoading,
  totalDataRows,
  enablePagination,
  selectedPage,
  enablePagePerRows,
  rowsPerList,
  selectedRowsPerPage,
  columns,
  rows,
  sortBy,
  sortOrder,
  handleChangePage,
  handleChangeRowsPerPage,
  handleSortByChange,
  handleSortOrderChange,
  bottomResultsCount,
  tableType,
}: CustomTableProps) {
  const [anchorElRowsPerPage, setAnchorElRowsPerPage] = React.useState<null | HTMLElement>(null);

  const handleSortChange = (currentSortKey: string, sortKey: string, currentSortOrder: string) => {
    if (currentSortKey !== sortKey) {
      handleSortByChange(sortKey);
      handleSortOrderChange(SortOrder.ASC);
    } else if (currentSortOrder === SortOrder.ASC) {
      handleSortOrderChange(SortOrder.DESC);
    } else {
      handleSortOrderChange(SortOrder.ASC);
    }
  };

  const getDisplayedRange = () => {
    const start = selectedPage === 1 ? 1 : (selectedPage - 1) * selectedRowsPerPage + 1;
    const end = Math.min(totalDataRows, selectedPage * selectedRowsPerPage);
    return `${start} - ${end} of ${totalDataRows} ${tableType}`;
  };

  return (
    <TableContainer>
      <Table aria-label="simple table" size="small">
        <CustomTableHead>
          <TableRow>
            {columns.map((e, _i) => (
              <CustomTableCell key={e.key} id={`${e.key}-column-id`} width={e.width} align={e.align} height="40px">
                <Box sx={{ display: 'flex', alignItems: 'center' }} justifyContent={e.headerAlign}>
                  <Box display="flex" alignItems="center" gap="7px" textAlign={e.headerAlign}>
                    {e.customComponent}
                    {!e.customComponent && !!e.name && (
                      <Typography variant="title6Bold" color={theme.palette.common.black}>
                        {e.name}
                      </Typography>
                    )}
                    {e.enableSort && (
                      <IconButton
                        role="button"
                        size="small"
                        onClick={() => handleSortChange(sortBy, e.key, sortOrder)}
                        aria-label={`Sort by ${e.name}`}
                      >
                        <Box
                          sx={{
                            transform: sortBy === e.key && sortOrder === SortOrder.ASC ? 'scaleX(-1)' : 'scaleX(1)',
                          }}
                          component="img"
                          src={sortBy === e.key && sortOrder === SortOrder.ASC ? SortIconAscSvg : SortIconDescSvg}
                        />
                      </IconButton>
                    )}
                  </Box>
                </Box>
              </CustomTableCell>
            ))}
          </TableRow>
        </CustomTableHead>
        {!isLoading && (
          <CustomTableBody>
            {rows.map((row) => (
              <React.Fragment key={uuidv4()}>
                {!row.expandedContent && (
                  <CustomTableRow
                    hover
                    sx={{
                      borderBottom: `1px solid ${theme.palette.divider}`,
                    }}
                  >
                    {columns.map((column, _columnIndex) => (
                      <CustomTableCell align={column.align} component="th" scope="row" key={column.key}>
                        {row[column.key] instanceof String ? (
                          row[column.key]
                        ) : (
                          <Typography variant="metaInter" color={theme.palette.common.black}>
                            {row[column.key]}
                          </Typography>
                        )}
                      </CustomTableCell>
                    ))}
                  </CustomTableRow>
                )}
                {row.expandedContent && (
                  <TableRow>
                    <TableCell
                      colSpan={columns.length}
                      sx={{
                        border: 0,
                        padding: 0,
                      }}
                    >
                      {row.expandedContent}
                    </TableCell>
                  </TableRow>
                )}
              </React.Fragment>
            ))}
          </CustomTableBody>
        )}

        {isLoading && (
          <TableBody>
            {Array.from(Array(10)).map(() => (
              <CustomTableRow hover key={uuidv4()}>
                {columns.map((column, _columnIndex) => (
                  <CustomTableCell align={column.align} component="th" scope="row" key={column.key} role="progressbar">
                    <Skeleton animation="wave" />
                  </CustomTableCell>
                ))}
              </CustomTableRow>
            ))}
          </TableBody>
        )}
        <TableFooter>
          <TableRow>
            <TableCell colSpan={columns.length} sx={{ borderBottomWidth: 0, padding: '8px 10px' }}>
              <Grid container>
                <Grid
                  item
                  xs={_.isUndefined(bottomResultsCount) ? 6 : 4}
                  display="flex"
                  justifyContent="flex-start"
                  mt={2}
                >
                  <Box>
                    <Stack
                      direction="row"
                      alignItems="center"
                      justifyContent="flex-end"
                      padding={1}
                      gap={2}
                      border={`1px solid ${theme.palette.shades.grayBorder}`}
                      borderRadius="8px"
                    >
                      {enablePagePerRows && (
                        <>
                          <Typography variant="tableRowRegular" color="shades.common.black">
                            Show {selectedRowsPerPage}
                          </Typography>
                          <IconButton
                            aria-label="row-per-page-expand-btn"
                            onClick={(event) => setAnchorElRowsPerPage(event.currentTarget)}
                            size="small"
                            sx={{ p: 0 }}
                            role="button"
                          >
                            <Box component="img" src={ExpandMoreSvg} />
                          </IconButton>
                          <Menu
                            aria-label="menu-appbar"
                            anchorEl={anchorElRowsPerPage}
                            anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
                            keepMounted={false}
                            transformOrigin={{ vertical: 'top', horizontal: 'right' }}
                            open={Boolean(anchorElRowsPerPage)}
                            onClose={() => setAnchorElRowsPerPage(null)}
                            role="menu"
                          >
                            {rowsPerList.map((e) => (
                              <MenuItem
                                id={`record-per-row-${e}`}
                                selected={e === selectedRowsPerPage}
                                onClick={() => {
                                  handleChangeRowsPerPage(e);
                                  setAnchorElRowsPerPage(null);
                                }}
                                key={e}
                                role="menuitem"
                              >
                                <Typography variant="meta" color="info" textAlign="center">
                                  {e}
                                </Typography>
                              </MenuItem>
                            ))}
                          </Menu>
                        </>
                      )}
                      <Divider orientation="vertical" flexItem sx={{ marginY: -1 }} />
                      <Typography variant="tableRowRegular" color="shades.common.black">
                        {getDisplayedRange()}
                      </Typography>
                    </Stack>
                  </Box>
                </Grid>
                <Grid
                  item
                  xs={_.isUndefined(bottomResultsCount) ? 6 : 4}
                  display="flex"
                  justifyContent="flex-end"
                  mt={2}
                >
                  {enablePagination && (
                    <CustomPagination
                      size="medium"
                      page={selectedPage}
                      count={totalDataRows && selectedRowsPerPage ? Math.ceil(totalDataRows / selectedRowsPerPage) : 1}
                      siblingCount={0}
                      boundaryCount={1}
                      onChange={(event, page) => handleChangePage(page)}
                      renderItem={(item) => (
                        <PaginationItem
                          {...item}
                          components={{
                            previous: () => <span>&larr; Prev</span>,
                            next: () => <span>Next &rarr;</span>,
                          }}
                        />
                      )}
                    />
                  )}
                </Grid>
              </Grid>
            </TableCell>
          </TableRow>
        </TableFooter>
      </Table>
    </TableContainer>
  );
}

export default CustomTable;
